import { diInject } from "stores/diContext";

import { OrderService } from "../services/order";
import { StripeService } from "../services/stripe";

import { computed, observable, toJS } from "mobx";
import { EventStore } from "./events";
import { TicketsStore } from "./ticket";
import { TicketItemStore } from "./ticket";

import PuchaserForm from "../forms/puchaser";
import QuestionForm from "../forms/question";
import AttendeesForm from "../forms/attendees";
import { CartStore } from "Apps/ticketing/stores/cart";
import { HttpFactory } from "utils/http";
import { FacebookPageShareUrlWay } from "consts/FacebookPageShareUrlWay";

import { __urls__ } from "config";
import { ImageBuilder } from "components/Image/ImageBuilder";
import { isEmptyOrNull } from "utils/string";
import { logger } from "stores/logger";
import { LText } from "Apps/shared/LText";
import { handleStripeErrorMessage } from "utils/buyService";

import { reCaptchaExecute } from "recaptcha-v3-react-function-async";

export const OrderStatus = {
  Ready: "ready",
  Pending: "pending",
  Done: "done",
  Done429: "done429",
  Fail: "fail",
  StripeEx: "stripe-ex",
};

export const ProcessStatus = {
  process_ready: "READY",
  process_checkout: "PROCESS_CHECKOUT",
  process_card: "PROCESS_CARD",
  process_payment: "PROCESS_PAYMENT",
  process_rsvp: "PROCESS_RSVP",
};

/**
 * 订单Store
 */
export class OrderStore {
  @diInject() orderService: OrderService;
  @diInject() eventStore: EventStore;
  @diInject() cartStore: CartStore;
  @diInject() stripeService: StripeService;

  @observable status = OrderStatus.Ready;
  @observable process_status = ProcessStatus.process_ready;
  purchaser: PuchaserForm = PuchaserForm.create();
  questions: QuestionForm = QuestionForm.create();
  attendees: AttendeesForm = AttendeesForm.create();
  stripe: any = null;
  payment_transaction_token: any = null;
  payment_method_id: any = null;
  payment_intent_id: any = null;
  last4: any = null;
  applePayMode: boolean = false;
  cardNumber: any = null;
  personData: any = null;

  setStripe(stripe: any) {
    this.stripe = stripe;
  }

  setCardNumber(cardNumber: any) {
    this.cardNumber = cardNumber;
  }

  setApplePayData(
    status: boolean,
    payment_method_id?: any,
    last4?: any,
    personData?: any
  ) {
    this.applePayMode = status;
    this.payment_method_id = payment_method_id;
    this.last4 = last4;
    this.personData = personData;
  }

  private createPaymentToken = async (): Promise<any> => {
    const { error, token } = this.applePayMode
      ? await this.stripe.createToken("person", this.personData)
      : await this.stripe.createToken(this.cardNumber);

    if (error) {
      this.stripeEx = error;
      this.status = OrderStatus.StripeEx;
      return null;
    } else {
      this.payment_transaction_token = token.id;
    }
  };

  private createPaymentMethod = async (): Promise<any> => {
    const { error, paymentMethod } = await this.stripe.createPaymentMethod({
      type: "card",
      card: this.cardNumber,
    });

    if (error) {
      this.stripeEx = error;
      this.status = OrderStatus.StripeEx;
      return null;
    } else {
      this.payment_method_id = paymentMethod.id;
      this.last4 = paymentMethod.card.last4;
    }
  };

  private handleStripe3DSecure = async (result): Promise<any> => {
    if (result.error) {
      this.stripeEx = result.error;
      this.status = OrderStatus.StripeEx;
      this.payment_method_id = null;
      this.payment_intent_id = result.error.payment_intent.id;
    } else {
      this.payment_intent_id = result.paymentIntent.id;
      this.payment_method_id = null;
    }
    await this.orderAndPay();
  };

  setPurchaserQuestionsFields() {
    const questions = this.eventStore.event.questions;
    const purchaser_questions = [];
    const selected_tickets_id = this.cartStore.items.keys();
    selected_tickets_id.forEach((tid) => {
      const qs = questions.filter(
        (q) => q.ask_to == "Purchaser" && q.applied_to_ticket_id.includes(tid)
      );
      purchaser_questions.push(...qs);
    });
    this.questions.createFields(purchaser_questions);
  }

  /**
   * 配置表单字段，这里只是改变几个字段的验证
   */
  setPuchaserFields() {
    const { event } = this.eventStore;
    if (event.need_collect_phone_number) {
      this.purchaser.enableValidators("customer_phone_number");
    } else {
      this.purchaser.disableValidators("customer_phone_number");
    }
    if (event.need_collect_zip_code) {
      this.purchaser.enableValidators("customer_zip_code");
    } else {
      this.purchaser.disableValidators("customer_zip_code");
    }
    if (this.cartStore.total_amount == 0) {
      this.purchaser.disableValidators("card_number").disableValidators("cvc");
    } else {
      this.purchaser.enableValidators("card_number").enableValidators("cvc");
    }
  }

  /**
   * 重置订单
   */
  clear() {
    this.order_id = null;
    ////重置状态
    this.status = OrderStatus.Ready;
    //重置表单
    this.purchaser.reset();
    //重置问题表单
    this.questions.removeAll();
    //重置出席者表单
    this.attendees.removeAll();
    this.stripeEx = null;
    this.error = null;
  }
  error;

  /*
   * stripe 异常
   */
  stripeEx: StripeError;

  order_id;

  private resolveFbTrackCode() {
    if (this.eventStore.event.is_set_conversion_tracking_code) {
      if ($("#facebook_track_code").length == 0) {
        $("body").append(
          `<div id="facebook_track_code" style="display:none"></div>`
        );
      }
      $("#facebook_track_code")
        .empty()
        .html(this.eventStore.event.facebook_conversion_tracking_code);
    }
  }

  /**
   * 提交订单
   */
  async submit() {
    this.stripeEx = null;
    this.status = OrderStatus.Pending;
    await this.createPaymentToken();

    if (!this.applePayMode) {
      await this.createPaymentMethod();
    }

    await this.orderAndPay(false);
  }

  async submitWithoutPay() {
    this.stripeEx = null;
    this.status = OrderStatus.Pending;
    await this.orderAndPay();
  }

  async orderAndPay(preview = false) {
    //const gtoken = await reCaptchaExecute(process.env.reCaptchaKey, 'auth');
    const gtoken = "";
    //这里的try是ts中async/await的操作，catch代表是Promise.catch
    // @ts-ignore
    try {
      const {
        event_id,
        event_schedule_id,
        shareParams: { __rn__, ...shareParams },
      } = this.eventStore;
      const {
        payment_method,
        discounts: discount_ids,
        total_amount,
        donations,
      } = this.cartStore;
      const {
        customer_email_address,
        customer_first_name,
        customer_last_name,
        customer_phone_number,
        customer_zip_code,
      } = this.purchaser.values();

      // @ts-ignore
      const sid = new URL(document.location).searchParams.get("sid");

      const question_answers = this.questions.values();
      const tickets = this.cartStore.items.values().map((t) => {
        const t_id = t.ticketStore.id;
        return {
          id: t_id,
          quantity: t.quantity,
          attendees: this.attendees.get(t_id),
        };
      });

      var payment_method_id;
      var bank_card_info;

      if (total_amount > 0) {
        bank_card_info = {
          address_zip: customer_zip_code,
          card_number: this.last4,
        };
        this.process_status = ProcessStatus.process_payment;
      } else {
        this.process_status = ProcessStatus.process_rsvp;
      }
      var data = {
        bank_card_info,
        order: {
          customer_email_address,
          customer_first_name,
          customer_last_name,
          customer_phone_number,
          customer_zip_code,
          event_id,
          event_schedule_id,
          payment_method,
          discount_ids,
          donations: donations.values(),
          question_answers,
          tickets,
        },
        payment_transaction_token: this.payment_transaction_token,
        payment_method_id: this.payment_method_id,
        stripe_payment_intent_id: this.payment_intent_id,
        order_id: this.order_id,
        gtoken,
        ...shareParams,
      };
      if (!!sid) {
        data["sponsor_code"] = sid;
      }
      if (!preview) {
        const res = await this.orderService.submitOrder(data);
        this.order_id = res.id;
        if (res.status === "Processing") {
          // for new stripe
          this.stripe
            .confirmCardPayment(res.stripe_payment_intent_client_secret, {
              payment_method: payment_method_id,
            })
            .then(this.handleStripe3DSecure);
        }
        if (res.status === "Complete") {
          this.resolveFbTrackCode();
          this.status = OrderStatus.Done;
        }
      }
      // this.resolveFbTrackCode();
      // this.status = OrderStatus.Done;
    } catch (ex) {
      if (ex.stripe) {
        this.stripeEx = ex.error;
        this.status = OrderStatus.StripeEx;
        return;
      }
      if (ex.status == 429) {
        this.status == OrderStatus.Done429;
        this.resolveFbTrackCode();
        return;
      }
      var response = ex.responseJSON;
      if (response && response.error) {
        const error = response.error;
        if (error) {
          if (error.code == 600010) {
            this.stripeEx = error;
            this.status = OrderStatus.StripeEx;
          } else {
            this.error = error;
            this.status = OrderStatus.Fail;
          }
          return;
        }
      }
      var message = ex.message;
      if (!message) {
        if (!ex.responseText) {
          message = LText.get("DEFAULT_ERROR");
        }
      }
      this.error = {
        message: handleStripeErrorMessage(message) || LText.get("SERVER_ERROR"),
      };
      this.status = OrderStatus.Fail;
      logger.exception(ex, {
        desc: "支付时发生未知异常,Apps/ticketing/stores/order.ts:217",
      });
    } finally {
      this.process_status = ProcessStatus.process_ready;
    }
  }

  getShareLink() {
    var theEvent = this.eventStore.event;
    const showShare = theEvent.is_set_social_promotions;
    let share_url = "";
    if (showShare) {
      let fbCfg = theEvent.facebook_page;
      let params: any = {};
      var shareId = this.order_id;
      if (shareId && shareId != "") {
        params.shareId = shareId;
      }
      var rn = this.eventStore.shareParams.__rn__;
      if (!isEmptyOrNull(rn)) {
        params.rn = rn;
      }
      var shareUrl = __urls__.event + "/" + theEvent.url_name;
      if (fbCfg.share_url_way == FacebookPageShareUrlWay.EventUrl) {
        shareUrl = __urls__.event + "/embed/" + theEvent.url_name;
      }
      if (fbCfg.share_url_way == FacebookPageShareUrlWay.CustomUrl) {
        params.redirect = fbCfg.share_url;
      }
      var p = $.param(params);
      share_url = shareUrl + (p == "" ? "" : "?" + p);
    }
    return share_url;
  }

  share() {
    return $.Deferred((dfd) => {
      var theEvent = this.eventStore.event;
      const name = theEvent.facebook_page.title || theEvent.name,
        description =
          theEvent.facebook_page.description ||
          $("<div>" + theEvent.long_description + "</div>").text(),
        picture = ImageBuilder.src(theEvent.image_url)
          .crop(theEvent.image_crop_info)
          .width(1200)
          .build(),
        link = this.getShareLink(),
        caption = __urls__.event.replace("https://", "");

      var FB = window["FB"];
      if (FB) {
        //使用sdk发表动态
        FB.ui(
          {
            method: "feed",
            display: "popup",
            name,
            link,
            picture,
            caption,
            description,
          },
          (ret) => {
            if (ret && !ret.error_code) {
              dfd.resolve();
            } else {
              dfd.reject();
            }
          }
        );
      } else {
        //通过跳转发表动态
        const fb_param = {
          app_id: __FB_APPID__,
          display: "popup",
          name,
          link,
          picture,
          caption,
          description,
          redirect_uri: "https://www.facebook.com",
        };
        var win = window.open(
          "https://www.facebook.com/dialog/feed?" + $.param(fb_param),
          "_blank"
        );
        if (!win) {
          top.location.href =
            "https://www.facebook.com/dialog/feed?" + $.param(fb_param);
        }
        dfd.resolve();
      }
    }).promise();
  }

  checkout() {
    this.setPuchaserFields();
    this.setPurchaserQuestionsFields();
  }

  @diInject("api") http: HttpFactory;

  getPrintLink() {
    //if (this.isPreview) {
    //    return '#';
    //}
    if (!this.order_id) {
      return null;
    }
    const customer_email_address = this.purchaser.customer_email_address.$; //.values();
    return this.http.url(
      `/orders/${
        this.order_id
      }/print_tickets?customer_email_address=${encodeURIComponent(
        customer_email_address
      )}`
    ).options.url;
  }
}



// WEBPACK FOOTER //
// ./src/Apps/ticketing/stores/order.ts